import { axiosObject, baseURL, HttpMethods, makeRequest } from '../helpers/http.helper';
import type {
  ImportResult,
  Supplier,
  SupplierInput,
  SupplierOrganizationOption,
  SupplierOrganizationOptionPurpose,
  SupplierPagination,
  SupplierQuery,
  SupplierUpdateInput,
} from '../types/supplier.type';

interface GenericResponse<T> {
  result: T;
}

export async function listSuppliers(params: SupplierQuery): Promise<SupplierPagination> {
  const response = await makeRequest({
    url: '/ops/suppliers',
    method: HttpMethods.GET,
    params,
  }) as GenericResponse<SupplierPagination>;
  return response.result;
}

export async function getSupplier(organizationId: string, supplierId: string): Promise<Supplier> {
  const response = await makeRequest({
    url: `/ops/suppliers/${supplierId}`,
    method: HttpMethods.GET,
    params: { organization_id: organizationId },
  }) as GenericResponse<Supplier>;
  return response.result;
}

export async function createSupplier(organizationId: string, input: SupplierInput): Promise<Supplier> {
  const response = await makeRequest({
    url: '/ops/suppliers',
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: input,
  }) as GenericResponse<Supplier>;
  return response.result;
}

export async function updateSupplier(
  organizationId: string,
  supplierId: string,
  input: SupplierUpdateInput,
): Promise<Supplier> {
  const response = await makeRequest({
    url: `/ops/suppliers/${supplierId}`,
    method: HttpMethods.PATCH,
    params: { organization_id: organizationId },
    data: input,
  }) as GenericResponse<Supplier>;
  return response.result;
}

export async function archiveSupplier(organizationId: string, supplierId: string): Promise<void> {
  await makeRequest({
    url: `/ops/suppliers/${supplierId}`,
    method: HttpMethods.DELETE,
    params: { organization_id: organizationId },
  });
}

export async function shareSupplier(
  organizationId: string,
  supplierId: string,
  targetOwnerOrgId: string,
): Promise<Supplier> {
  const response = await makeRequest({
    url: `/ops/suppliers/${supplierId}/share`,
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: { target_owner_org_id: targetOwnerOrgId },
  }) as GenericResponse<Supplier>;
  return response.result;
}

export async function downloadSupplierImportTemplate(organizationId: string): Promise<Blob> {
  const response = await axiosObject.get(`${baseURL}/ops/suppliers/import-template`, {
    params: { organization_id: organizationId },
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function importSuppliers(organizationId: string, file: File): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosObject.post(`${baseURL}/ops/suppliers/import`, formData, {
    params: { organization_id: organizationId },
  });
  return (response.data as GenericResponse<ImportResult>).result;
}

export async function listSupplierOrganizationOptions(
  organizationId: string,
  purpose: SupplierOrganizationOptionPurpose,
  search = '',
): Promise<SupplierOrganizationOption[]> {
  const response = await makeRequest({
    url: '/ops/suppliers/organization-options',
    method: HttpMethods.GET,
    params: { organization_id: organizationId, purpose, search: search || undefined },
  }) as GenericResponse<SupplierOrganizationOption[]>;
  return response.result;
}

export interface SyncCollectionSitesResult {
  created: unknown[];
  skipped: Array<{ collection_site_id: string; reason: string }>;
}

/**
 * Pull every not-yet-synced collection site from the supplier's linked waste outlet.
 * Idempotent server-side: repeats skip what already exists.
 */
export async function syncOutletCollectionSites(
  organizationId: string,
  supplierId: string
): Promise<SyncCollectionSitesResult> {
  const response = (await makeRequest({
    url: `/ops/suppliers/${supplierId}/sync-collection-sites`,
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
  })) as { result: SyncCollectionSitesResult };
  return response.result;
}
